import { useState, useEffect } from "react";
import React from "react";
import profilePicture from "../assets/User/default-icons/icon-1.png";
import PhotoItem from "./Photo";
import type { Photo } from "../types/photo";
import LikeButton from "./LikeButton";
import { IoLocationOutline } from "react-icons/io5";
import { MdDateRange } from "react-icons/md";
import type { SelectedPhoto } from "../pages/MapPage";
import CommentList from "./comment/CommentList";
import { Utils } from "../utilities/Utils";
import { fetchAvatar } from "../services/userService";
import { TiUserOutline } from "react-icons/ti";

interface SidebarDrawerProps {
  selectedMarker: SelectedPhoto;
  onLikeToggle: (newLiked: boolean, newLikeCount: number) => void;
  onToggleDrawer?: () => void;
}

const SidebarDrawer = ({ selectedMarker, onLikeToggle }: SidebarDrawerProps) => {
  const [expanded, setExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState<"details" | "comments">("details");
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [startX, setStartX] = useState<number | null>(null);
  const [avatar, setAvatar] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAvatar(selectedMarker.uploadedBy.userId);
  }, []);

  if (!selectedMarker) {
    return (
      <div className="w-1/4 h-[calc(100vh-64px)] p-4 bg-background border-l flex items-center justify-center text-muted-foreground">
        Select a marker to see details
      </div>
    );
  }

  const fullText = selectedMarker.description || "";
  const shortText = fullText.length > 100 ? fullText.substring(0, 100) : fullText;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setStartX(e.touches[0].clientX);
  };

  const getAvatar = async (userId: number) => {
    const response = await fetchAvatar(userId);
    setAvatar(response.avatar);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startX !== null) {
      const endX = e.changedTouches[0].clientX;
      const diff = endX - startX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          setCurrentPage("details");
        } else {
          setCurrentPage("comments");
        }
      }
      setStartX(null);
    }
  };

  return (
    <div
      className="relative p-4 scrollbar-hide"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {/* Details section */}
      {(!isMobile || currentPage === "details") && (
        <>
          <div className="bg-secondary border border-accent rounded-2xl shadow-md mb-6 p-6">
            <h2 className="text-lg text-accentAlt font-bold text-center">
              {selectedMarker.title || "Untitled Photo"}
            </h2>
            <p className="text-sm text-primary font-normal mt-3 leading-relaxed text-left whitespace-pre-wrap">
              {expanded || fullText.length <= 100 ? fullText : shortText + "..."}
              {fullText.length > 100 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="inline text-primary font-medium ml-1 hover:underline transition-all duration-150"
                >
                  {expanded ? "less" : "more"}
                </button>
              )}
            </p>

            <div className="grid grid-cols-2 gap-3 text-sm text-accentAlt mt-6">
              {/* Like button */}
              <LikeButton
                photoId={selectedMarker.photoId}
                initialLiked={selectedMarker.liked}
                initialLikeCount={selectedMarker.likeCount ?? 0}
                size="text-sm"
                iconSize="w-5 h-5"
                onToggle={onLikeToggle}
              />
              {/* Date info */}
              <div className="flex items-center gap-2">
                {React.createElement(MdDateRange as React.ElementType, { className: "w-5 h-5" })}
                <span>
                  {selectedMarker.timestamp
                    ? Utils.formatDate(selectedMarker.timestamp, true)
                    : "Unknown"}
                </span>
              </div>
              {/* Location info */}
              <div className="flex items-center gap-2 col-span-2 text-xs mt-2">
                {React.createElement(IoLocationOutline as React.ElementType, { className: "w-5 h-5 shrink-0" })}
                <span className="break-words whitespace-pre-wrap">
                  {selectedMarker.address || "Unknown"}
                </span>
              </div>
            </div>
          </div>

          {/* Photo preview */}
          <div className="bg-secondary border border-accent rounded-lg shadow-md mb-4 p-4">
            <PhotoItem photo={selectedMarker} />
          </div>

          {/* Uploader info */}
          <div className="bg-secondary border border-accent rounded-lg shadow-md mb-4 p-4 flex flex-col items-center">
            {avatar &&
              <img
                className="w-16 h-16 rounded-full"
                src={avatar}
                alt="user avatar"
                onError={() => getAvatar(selectedMarker.uploadedBy.userId)}
              />
            }
            {!avatar &&
              <TiUserOutline className="w-16 h-16 text-white rounded-full mb-2" />
            }
            <p className="font-semibold text-accentAlt">{selectedMarker.uploaderName}</p>
            <p className="text-sm text-accentAlt">{selectedMarker.uploadedBy.country}</p>
          </div>
        </>
      )}

      {/* Comments section */}
      {(!isMobile || currentPage === "comments") && (
        <div className="bg-secondary border border-accent rounded-lg shadow-md mb-4 p-4">
          <CommentList photoId={selectedMarker.photoId} />
        </div>
      )}

      {/* Page switcher dots for mobile view */}
      {isMobile && (
        <div className="flex justify-center items-center mt-2 space-x-2">
          <span
            onClick={() => setCurrentPage("details")}
            className={`cursor-pointer text-xl ${currentPage === "details" ? "text-primary" : "text-gray-400"}`}
          >
            ●
          </span>
          <span
            onClick={() => setCurrentPage("comments")}
            className={`cursor-pointer text-xl ${currentPage === "comments" ? "text-primary" : "text-gray-400"}`}
          >
            ●
          </span>
        </div>
      )}
    </div>
  );
};

export default SidebarDrawer;